import Layout from 'components/layout/Layout'
import { useUser } from 'contexts/UserContext'
import useModules from 'hooks/useModules'
import Login from 'pages/Login/Login'
import Logout from 'pages/Login/Logout'
import ForgotPassword from 'pages/Login/ForgotPassword'
import ResetPassword from 'pages/Login/ResetPassword'
import PropTypes from 'prop-types'
import { lazy, Suspense } from 'react'
import { ToastContainer } from 'react-toastify'
import { Redirect, Route, Switch } from 'wouter'
import { isAdminRole, isSuperadminRole } from 'utils/roles'

// Las páginas del panel se descargan al abrirlas (cada una en su propio fichero),
// así el login y la primera carga no arrastran recharts, tablas, etc. Las de
// login/recuperar contraseña van en el bundle principal para que salgan al instante.
//
// Tras un despliegue, una pestaña abierta con la versión anterior puede pedir un
// fichero de página que ya no existe en el servidor: se recarga una vez para
// coger la versión nueva (la marca evita bucles si el fallo es otro).
const RELOAD_FLAG = 'chunk-reload'
const lazyPage = (load) => lazy(() => load()
  .then((module) => {
    try { sessionStorage.removeItem(RELOAD_FLAG) } catch { /* sin storage */ }
    return module
  })
  .catch((error) => {
    let reloaded = true
    try { reloaded = !!sessionStorage.getItem(RELOAD_FLAG) } catch { /* sin storage */ }
    if (!reloaded) {
      try { sessionStorage.setItem(RELOAD_FLAG, '1') } catch { /* sin storage */ }
      window.location.reload()
      return new Promise(() => {})
    }
    throw error
  }))

const Dashboard             = lazyPage(() => import('pages/Dashboard/Dashboard'))
const UserViewDetail        = lazyPage(() => import('./User/UserViewDetail'))
const UserViewGrid          = lazyPage(() => import('./User/UserViewGrid'))
const AssistantsPage        = lazyPage(() => import('./Assitants/AssistantsPage'))
const AssistantDetailPage   = lazyPage(() => import('./Assitants/AssistantsDetailPage'))
const AssistantsCreatePage  = lazyPage(() => import('./Assitants/AssistantsCreatePage'))
const VectorStoresPage      = lazyPage(() => import('./VectorStores/VectorStoresPage'))
const VectorStoreCreatePage = lazyPage(() => import('./VectorStores/VectorStoreCreatePage'))
const VectorStoreEditPage   = lazyPage(() => import('./VectorStores/VectorStoreEditPage'))
const ChatPage              = lazyPage(() => import('./Conversations/ChatPage'))
const StatisticsPage        = lazyPage(() => import('./Statistics/StatisticsPage'))
const CentralitaPage        = lazyPage(() => import('./Centralita/CentralitaPage'))
const ContactsPage          = lazyPage(() => import('./Contacts/ContactsPage'))
const AdminPage             = lazyPage(() => import('./Admin/AdminPage'))
const CostsPage             = lazyPage(() => import('./Costs/CostsPage'))
const NotificationsPage     = lazyPage(() => import('./Notifications/NotificationsPage'))
const ServersPage           = lazyPage(() => import('./Servers/ServersPage'))
const ServerDetailPage      = lazyPage(() => import('./Servers/ServerDetailPage'))
const ActivityPage          = lazyPage(() => import('./Activity/ActivityPage'))

// ── Ruta privada básica ────────────────────────────────────────────────────────
const PrivateRoute = ({ isLoggedIn, ...props }) =>
  isLoggedIn ? (
    <Layout>
      <Route {...props} />
    </Layout>
  ) : (
    <Redirect to="/login" />
  )

PrivateRoute.propTypes = { isLoggedIn: PropTypes.bool }

// ── Ruta solo para Admin y Superadmin ────────────────────────────────────────
const AdminRoute = ({ isLoggedIn, ...props }) => {
  const { user } = useUser()
  if (!isLoggedIn) return <Redirect to="/login" />
  if (!isAdminRole(user?.role)) return <Redirect to="/" />
  return (
    <Layout>
      <Route {...props} />
    </Layout>
  )
}

AdminRoute.propTypes = { isLoggedIn: PropTypes.bool }

// ── Ruta solo para Superadmin (Servidores, registro de actividad) ────────────
const SuperadminRoute = ({ isLoggedIn, ...props }) => {
  const { user } = useUser()
  if (!isLoggedIn) return <Redirect to="/login" />
  if (!isSuperadminRole(user?.role)) return <Redirect to="/" />
  return (
    <Layout>
      <Route {...props} />
    </Layout>
  )
}

SuperadminRoute.propTypes = { isLoggedIn: PropTypes.bool }

// ── Ruta privada + control de módulo ──────────────────────────────────────────
// Si el usuario no tiene el módulo requerido, redirige al dashboard
const ModuleRoute = ({ isLoggedIn, requiredModule, ...props }) => {
  const { hasModule } = useModules()

  if (!isLoggedIn) return <Redirect to="/login" />
  if (requiredModule && !hasModule(requiredModule)) return <Redirect to="/" />

  return (
    <Layout>
      <Route {...props} />
    </Layout>
  )
}

ModuleRoute.propTypes = {
  isLoggedIn:     PropTypes.bool,
  requiredModule: PropTypes.string,
}

// ── Ruta de login ──────────────────────────────────────────────────────────────
const LoginRoute = ({ isLoggedIn, ...props }) =>
  isLoggedIn ? <Redirect to="/" /> : <Route path="/login" component={Login} />

LoginRoute.propTypes = { isLoggedIn: PropTypes.bool }

// ── App ────────────────────────────────────────────────────────────────────────
export default function App() {
  const { user } = useUser()
  const isLoggedIn = user && !!user.token

  return (
    <>
      {/* Red de seguridad: cada página ya muestra su propio indicador dentro del Layout */}
      <Suspense fallback={null}>
      <Switch>
        {/* Auth */}
        <LoginRoute isLoggedIn={isLoggedIn} path="/login" component={Login} />
        {/* Recuperar contraseña — públicas, accesibles con o sin sesión */}
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <PrivateRoute isLoggedIn={isLoggedIn} path="/logout" component={Logout} />

        {/* Dashboard — siempre accesible si está logado */}
        <PrivateRoute isLoggedIn={isLoggedIn} path="/" component={Dashboard} />

        {/* Usuarios — solo admins (role 1 o 2, controlado en el layout/menu) */}
        <PrivateRoute isLoggedIn={isLoggedIn} path="/users/grid" component={UserViewGrid} />
        <PrivateRoute isLoggedIn={isLoggedIn} path="/users/:id"  component={UserViewDetail} />

        {/* ── Módulo CHATBOT ── */}
        <ModuleRoute isLoggedIn={isLoggedIn} requiredModule="chatbot"
          path="/assistants"              component={AssistantsPage} />
        <ModuleRoute isLoggedIn={isLoggedIn} requiredModule="chatbot"
          path="/assistants/create"       component={AssistantsCreatePage} />
        <ModuleRoute isLoggedIn={isLoggedIn} requiredModule="chatbot"
          path="/assistants/:assistantId" component={AssistantDetailPage} />

        <ModuleRoute isLoggedIn={isLoggedIn} requiredModule="chatbot"
          path="/vectorstores"                     component={VectorStoresPage} />
        <ModuleRoute isLoggedIn={isLoggedIn} requiredModule="chatbot"
          path="/vectorstores/create"              component={VectorStoreCreatePage} />
        <ModuleRoute isLoggedIn={isLoggedIn} requiredModule="chatbot"
          path="/vectorstores/:vectorStoreId"      component={VectorStoreEditPage} />
        <ModuleRoute isLoggedIn={isLoggedIn} requiredModule="chatbot"
          path="/vectorstores/edit/:vectorStoreId" component={VectorStoreEditPage} />

        <ModuleRoute isLoggedIn={isLoggedIn} requiredModule="chatbot"
          path="/conversations/:projectId/:threadId" component={ChatPage} />
        <ModuleRoute isLoggedIn={isLoggedIn} requiredModule="chatbot"
          path="/conversations/:projectId"           component={ChatPage} />
        <ModuleRoute isLoggedIn={isLoggedIn} requiredModule="chatbot"
          path="/conversations"                      component={ChatPage} />

        <ModuleRoute isLoggedIn={isLoggedIn} requiredModule="chatbot"
          path="/statistics" component={StatisticsPage} />

        {/* ── Módulo CENTRALITA ── */}
        <ModuleRoute isLoggedIn={isLoggedIn} requiredModule="centralita"
          path="/centralita" component={CentralitaPage} />

        <ModuleRoute isLoggedIn={isLoggedIn} requiredModule="centralita"
          path="/contactos" component={ContactsPage} />

        {/* ── Panel Admin ── */}
        <AdminRoute isLoggedIn={isLoggedIn} path="/admin" component={AdminPage} />

        {/* Informe de costes — solo Admin */}
        <AdminRoute isLoggedIn={isLoggedIn} path="/costes" component={CostsPage} />

        {/* Avisos e informe mensual por correo — solo Admin */}
        <AdminRoute isLoggedIn={isLoggedIn} path="/avisos" component={NotificationsPage} />

        {/* Servidores (estado, servicios y reinicio vía n8n) — solo Superadmin */}
        <SuperadminRoute isLoggedIn={isLoggedIn} path="/servidores" component={ServersPage} />
        <SuperadminRoute isLoggedIn={isLoggedIn} path="/servidores/:slug" component={ServerDetailPage} />

        {/* Registro de actividad — solo Superadmin */}
        <SuperadminRoute isLoggedIn={isLoggedIn} path="/actividad" component={ActivityPage} />
      </Switch>
      </Suspense>

      <ToastContainer />
    </>
  )
}

PrivateRoute.propTypes = { isLoggedIn: PropTypes.bool }
LoginRoute.propTypes  = { isLoggedIn: PropTypes.bool }